import { Position, Collision } from 'shared/src/ecs/components';
import { Depths, RenderObject } from '../types';
import { Renderable } from '../components';
import { contrast01, fbm1D, getRadius, noise2D01, rgb, ui32ToCol, warpedFbm2D01 } from 'shared/src/utils';
import { AnyPoint } from 'shared/src/types';
import { drawPolygon } from '../utils';
import { AsteroidVariant } from 'shared/src/content';
import { nailToContainer } from 'src/util';

const renderAsteroid: RenderObject = (scene, eid) => {
  // const x = Position.x[eid];
  // const y = Position.y[eid];
  // const world = scene.world;
  // const visualSeed = Number(world.getSeed(eid)) * 100;
  // const roughness = Renderable.roughness[eid];
  // const distortion = Renderable.distortion[eid];

  // const radius = Collision.radius[eid];
  // const colour = Renderable.col[eid];

  // const tuning = getAsteroidRenderTuning(eid);

  // const points = generateAsteroidPolygonPoints(radius, (theta01) => asteroidEdgeNoise01(theta01, visualSeed, {
  //   frequency: 2 + roughness * 4 * tuning.edgeFrequency,
  //   octaves: 3 + Math.round(roughness * 3),
  //   gain: 0.45 + roughness * 0.2 * tuning.edgeGain,
  // }), {
  //   pointCount: Math.max(24, Math.round(radius * 0.9)),
  //   distortionAmount: 0.08 + distortion * 0.18 * tuning.distortionAmount,
  //   minScale: 0.78,
  // });

  // const container = scene.add.container(x, y);
  // const graphics = scene.add.graphics();
  // graphics.fillStyle(ui32ToCol(colour), 1);
  // drawPolygon(graphics, points)
  // graphics.fillPath();

  // const maskGraphics = scene.add.graphics();
  // maskGraphics.fillStyle(0xffffff, 1);
  // drawPolygon(maskGraphics, points);
  // maskGraphics.fillPath();

  // container.add(maskGraphics);
  // maskGraphics.setVisible(false);

  // const geomMask = maskGraphics.createGeometryMask();

  // container.add(graphics);

  // const patchGraphics = scene.add.graphics();
  // drawAsteroidSurfacePatches(patchGraphics, points, radius, visualSeed, eid);
  // patchGraphics.setMask(geomMask);
  // container.add(patchGraphics);
  // container.setDepth(Depths.PLANETS);
  // return container;

  const x = Position.x[eid];
  const y = Position.y[eid];
  const world = scene.world;
  const visualSeed = Number(world.getSeed(eid)) * 100;

  const radius = Collision.radius[eid];
  const colour = Renderable.col[eid];

  const tuning = getAsteroidFieldTuning(eid);

  const points = generateAsteroidPolygonPoints(
    radius,
    (theta01) => asteroidEdgeFromField01(theta01, visualSeed, tuning),
    {
      pointCount: tuning.pointCount,
      distortionAmount: tuning.distortionAmount,
      minScale: 0.78,
    },
  );

  const container = scene.add.container(x, y);

  const bodyGraphics = scene.add.graphics();
  bodyGraphics.fillStyle(ui32ToCol(colour), 1);
  drawPolygon(bodyGraphics, points);
  bodyGraphics.fillPath();
  container.add(bodyGraphics);

  const maskGraphics = scene.add.graphics();
  maskGraphics.fillStyle(0xffffff, 1);
  drawPolygon(maskGraphics, points);
  maskGraphics.fillPath();

  maskGraphics.setVisible(false);
  const maskedContainer = nailToContainer(container, maskGraphics);

  const geomMask = maskGraphics.createGeometryMask();

  const patchGraphics = scene.add.graphics();
  drawAsteroidSurfacePatches(
    patchGraphics,
    points,
    radius,
    visualSeed,
    eid,
  );
  patchGraphics.setMask(geomMask);
  maskedContainer.add(patchGraphics);

  const fissureGraphics = scene.add.graphics();
  drawAsteroidFissures(fissureGraphics, points, radius, visualSeed, eid);
  fissureGraphics.setMask(geomMask);
  maskedContainer.add(fissureGraphics);

  maskedContainer.setDepth(Depths.PLANETS);
  return maskedContainer;
};

export default renderAsteroid;

const asteroidField01 = (
  nx: number,
  ny: number,
  seed: number,
  tuning: AsteroidFieldTuning,
): number => {
  return warpedFbm2D01(nx * tuning.scale, ny * tuning.scale, seed, {
    warpAmp: tuning.warpAmp,
    warpFreq: tuning.warpFreq,
    fbm: {
      octaves: tuning.octaves,
      gain: tuning.gain,
      lacunarity: tuning.lacunarity,
    },
  });
};

const asteroidEdgeFromField01 = (
  theta01: number,
  seed: number,
  tuning: AsteroidFieldTuning,
): number => {
  const theta = theta01 * Math.PI * 2;

  const nx = Math.cos(theta);
  const ny = Math.sin(theta);

  return asteroidField01(nx, ny, seed, tuning);
};


type AsteroidPolygonOptions = {
  // How many points will be generated
  pointCount?: number;
  // How much the distortion affects the line (up or down)
  // 0.08 -> 0.25
  distortionAmount?: number;
  minScale?: number;
};

const generateAsteroidPolygonPoints = (
  radius: number,
  edgeNoiseFn: (theta01: number) => number,
  options: AsteroidPolygonOptions = {},
): Array<AnyPoint> => {
  const {
    pointCount = 48,
    distortionAmount = 0.14,
    minScale = 0.78,
  } = options;

  const points: Array<AnyPoint> = [];

  for (let i = 0; i < pointCount; i += 1) {
    const theta01 = i / pointCount;
    const theta = theta01 * Math.PI * 2;

    const edge01 = edgeNoiseFn(theta01);

    const radialScale = Math.max(
      minScale,
      1 + (edge01 - 0.5) * 2 * distortionAmount,
    );

    const r = radius * radialScale;

    points.push({
      x: Math.cos(theta) * r,
      y: Math.sin(theta) * r,
    });
  }

  return points;
};

const getAsteroidLocalRadius = (
  theta01: number,
  baseRadius: number,
  seed: number,
  tuning: AsteroidFieldTuning,
  minScale = 0.78,
): number => {
  const edge01 = asteroidEdgeFromField01(theta01, seed, tuning);

  const radialScale = Math.max(
    minScale,
    1 + (edge01 - 0.5) * 2 * tuning.distortionAmount,
  );

  return baseRadius * radialScale;
};

const doesCircleFitPolygon = (
  cx: number,
  cy: number,
  r: number,
  polygon: Array<AnyPoint>,
  samples = 12,
): boolean => {
  for (let i = 0; i < samples; i += 1) {
    const theta = (i / samples) * Math.PI * 2;
    const px = cx + Math.cos(theta) * r;
    const py = cy + Math.sin(theta) * r;

    if (!isPointInPolygon(px, py, polygon)) {
      return false;
    }
  }

  return true;
};


const isPointInPolygon = (x: number, y: number, polygon: Array<AnyPoint>): boolean => {
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersects =
      yi > y !== yj > y &&
      x < ((xj - xi) * (y - yi)) / (yj - yi + Number.EPSILON) + xi;

    if (intersects) inside = !inside;
  }

  return inside;
};

const drawAsteroidSurfacePatches = (
  graphics: Phaser.GameObjects.Graphics,
  polygon: Array<AnyPoint>,
  radius: number,
  seed: number,
  eid: number,
): void => {
  const tuning = getAsteroidFieldTuning(eid);
  const accentColour = ui32ToCol(Renderable.accentCol[eid]);
  const contrast = Renderable.contrast[eid] || 0.5;

  graphics.fillStyle(accentColour, tuning.patchAlpha);

  for (let py = -radius; py <= radius; py += tuning.step) {
    for (let px = -radius; px <= radius; px += tuning.step) {
      const jitterAmount = tuning.step * 0.45;

      const sx =
        px +
        (noise2D01(px * 0.17, py * 0.17, seed + 12001) - 0.5) *
          2 *
          jitterAmount;

      const sy =
        py +
        (noise2D01(px * 0.17, py * 0.17, seed + 13001) - 0.5) *
          2 *
          jitterAmount;

      if (!isPointInPolygon(sx, sy, polygon)) continue;

      const nx = sx / radius;
      const ny = sy / radius;

      const field01 = asteroidField01(nx, ny, seed + 5000, tuning);

      const theta = Math.atan2(sy, sx);
      const wrappedTheta = theta < 0 ? theta + Math.PI * 2 : theta;
      const theta01 = wrappedTheta / (Math.PI * 2);

      const localRadius = getAsteroidLocalRadius(theta01, radius, seed, tuning);
      const pointRadius = Math.sqrt(sx * sx + sy * sy);
      const edgeProximity = pointRadius / localRadius; // ~1 near the edge

      const maxPatchCenterFrac = 1 - tuning.edgeMarginFrac;

      if (edgeProximity > maxPatchCenterFrac) continue;

      // Bias patches a little toward interesting edge-related structure
      const combined =
        field01 * 0.8 +
        (1 - Math.abs(edgeProximity - 0.82)) * 0.2;

      const shapedMask = contrast01(combined, contrast * 1.5);

      if (shapedMask < tuning.patchThreshold) continue;

      const sizeNoise = noise2D01(nx * 3.1, ny * 3.1, seed + 9000);
      const blobRadius =
        tuning.patchRadiusMin +
        (tuning.patchRadiusMax - tuning.patchRadiusMin) * sizeNoise;

      // if (!doesCircleFitPolygon(sx, sy, blobRadius, polygon, 10)) continue;

      graphics.fillCircle(sx, sy, blobRadius);
    }
  }
};

const asteroidFissureField01 = (
  nx: number,
  ny: number,
  seed: number,
  tuning: AsteroidFieldTuning,
): number => {
  return warpedFbm2D01(nx * tuning.fissureScale, ny * tuning.fissureScale, seed + 2000, {
    warpAmp: tuning.fissureWarpAmp,
    warpFreq: tuning.fissureWarpFreq,
    fbm: {
      octaves: tuning.fissureOctaves,
      gain: tuning.fissureGain,
      lacunarity: tuning.fissureLacunarity,
    },
  });
};

const fissureMask01 = (
  nx: number,
  ny: number,
  seed: number,
  tuning: AsteroidFieldTuning,
): number => {
  const v = asteroidFissureField01(nx, ny, seed, tuning);
  const dist = Math.abs(v - tuning.fissureBandCenter);

  // 1 when exactly on the band center, fades out to 0
  const mask = 1 - Math.min(1, dist / Math.max(tuning.fissureBandWidth, 0.0001));
  return mask;
};

const drawAsteroidFissures = (
  graphics: Phaser.GameObjects.Graphics,
  polygon: Array<AnyPoint>,
  radius: number,
  seed: number,
  eid: number,
): void => {
  const tuning = getAsteroidFieldTuning(eid);
  const contrast = Renderable.contrast[eid] || 0.5;

  graphics.lineStyle(2, tuning.fissureColour, tuning.fissureAlpha);

  for (let py = -radius; py <= radius; py += tuning.fissureStep) {
    for (let px = -radius; px <= radius; px += tuning.fissureStep) {
      const jitterAmount = tuning.fissureStep * 0.45;

      const sx =
        px +
        (noise2D01(px * 0.21, py * 0.21, seed + 3001) - 0.5) *
          2 *
          jitterAmount;

      const sy =
        py +
        (noise2D01(px * 0.21, py * 0.21, seed + 4001) - 0.5) *
          2 *
          jitterAmount;

      if (!isPointInPolygon(sx, sy, polygon)) continue;

      const nx = sx / radius;
      const ny = sy / radius;

      const mainField = asteroidField01(nx, ny, seed, tuning);
      const crackField = fissureMask01(nx, ny, seed, tuning);

      const combined = crackField * (0.7 + (1 - mainField) * 0.3);
      const shaped = contrast01(combined, contrast * 1.2);

      if (shaped < 0.72) continue;

      const { dx, dy } = sampleFissureGradient(nx, ny, seed, tuning, 0.02);

      // tangent direction = perpendicular to gradient
      let tx = -dy;
      let ty = dx;

      const tLen = Math.hypot(tx, ty);
      if (tLen < 0.0001) continue;

      tx /= tLen;
      ty /= tLen;

      const sizeNoise = noise2D01(nx * 7.1, ny * 7.1, seed + 5001);
      const halfLen =
        tuning.fissureRadiusMin +
        (tuning.fissureRadiusMax - tuning.fissureRadiusMin) * sizeNoise;

      const x1 = sx - tx * halfLen;
      const y1 = sy - ty * halfLen;
      const x2 = sx + tx * halfLen;
      const y2 = sy + ty * halfLen;

      graphics.beginPath();
      graphics.moveTo(x1, y1);
      graphics.lineTo(x2, y2);
      graphics.strokePath();
    }
  }
};

type AsteroidFieldTuning = {
  scale: number;
  octaves: number;
  gain: number;
  lacunarity: number;
  warpAmp: number;
  warpFreq: number;

  distortionAmount: number;
  pointCount: number;

  patchThreshold: number;
  patchAlpha: number;
  patchRadiusMin: number;
  patchRadiusMax: number;
  step: number;

  edgeMarginFrac: number;

  fissureScale: number;
  fissureOctaves: number;
  fissureGain: number;
  fissureLacunarity: number;
  fissureWarpAmp: number;
  fissureWarpFreq: number;
  
  fissureBandCenter: number;
  fissureBandWidth: number;
  fissureColour: number;
  fissureAlpha: number;
  fissureStep: number;
  fissureRadiusMin: number;
  fissureRadiusMax: number;
};

const getAsteroidFieldTuning = (eid: number): AsteroidFieldTuning => {
  const roughness = Renderable.roughness[eid];
  const pattern = Renderable.pattern[eid];
  const distortion = Renderable.distortion[eid];
  const contrast = Renderable.contrast[eid] || 0.5;
  const variant = Renderable.variant[eid];
  const accent = Renderable.accentCol[eid];
  const radius = getRadius(eid);

  let scaleMult = 1;
  let gainMult = 1;
  let warpAmpMult = 1;
  let edgeMarginFrac = 0;
  
  let thresholdOffset = 0;
  let patchAlphaMult = 1;
  let patchRadiusMinMult = 1;
  let patchRadiusMaxMult = 1;

  let fissureBandMult = 1;
  let fissureScaleMult = 1;
  let fissureGainMult = 1;
  let fissureWarpAmpMult = 1;
  let fissureWarpFreqMult = 1;
  let fissureRadiusMinMult = 1;
  let fissureRadiusMaxMult = 1;
  let fissureColour = 0x000000;
  let fissureAlphaMult = 1;

  switch (variant) {
    case AsteroidVariant.CHUNKY:
      fissureBandMult = 8;
      fissureWarpAmpMult = 0.2;
      fissureWarpFreqMult = 0.3;
      fissureGainMult = 0.5;
      fissureRadiusMaxMult = 2;
      fissureColour = accent;
      
      patchAlphaMult = 0.5
      gainMult = 10;
      break;
    case AsteroidVariant.POROUS:
      scaleMult = 1.2;
      warpAmpMult = 1.2;
      thresholdOffset = -0.04;
      break;
    case AsteroidVariant.CARBON:
      scaleMult = 0.6;
      thresholdOffset = 0.05;
      edgeMarginFrac = 0.05;
      fissureAlphaMult = 0;
      break;
    case AsteroidVariant.METALLIC:
      gainMult = 0.5;
      warpAmpMult = 0.5;
      thresholdOffset = 0.12;

      patchRadiusMaxMult = 3;
      patchAlphaMult = 0.3;

      fissureBandMult = 2;
      fissureWarpAmpMult = 3;
      fissureWarpFreqMult = 3;
      // fissureGainMult = 0.5;
      fissureRadiusMaxMult = 5;
      fissureRadiusMinMult = 5;
      fissureColour = rgb().fromUi32(accent).multiply(0.5).ui32();
      break;
  }

  return {
    scale: (0.9 + pattern * 2.0) * scaleMult,
    octaves: 4 + Math.round(roughness * 2),
    gain: (0.5 + roughness * 0.15) * gainMult,
    lacunarity: 2,
    warpAmp: (0.15 + pattern * 0.35) * warpAmpMult,
    warpFreq: 1,

    distortionAmount: 0.08 + distortion * 0.18,
    pointCount: Math.max(24, Math.round(radius * 0.9)),

    patchThreshold: 0.62 + thresholdOffset,
    patchAlpha: 0.22 * patchAlphaMult,
    patchRadiusMin: Math.max(2, radius * 0.06) * patchRadiusMinMult,
    patchRadiusMax: Math.max(4, radius * 0.14) * patchRadiusMaxMult,
    step: Math.max(4, radius * 0.12),

    edgeMarginFrac,

    fissureScale: 2.2 * fissureScaleMult,
    fissureOctaves: 5,
    fissureGain: 0.55 * fissureGainMult,
    fissureLacunarity: 2,
    fissureWarpAmp: 0.22 * fissureWarpAmpMult,
    fissureWarpFreq: 1.4 * fissureWarpFreqMult,
    
    fissureBandCenter: 0.52,
    fissureBandWidth: 0.03 * fissureBandMult,
    fissureColour,
    fissureAlpha: 0.22 * fissureAlphaMult,
    fissureStep: Math.max(2, radius * 0.05),
    fissureRadiusMin: Math.max(1, radius * 0.035) * fissureRadiusMinMult,
    fissureRadiusMax: Math.max(2, radius * 0.048) * fissureRadiusMaxMult,
  };
};

const sampleFissureGradient = (
  nx: number,
  ny: number,
  seed: number,
  tuning: AsteroidFieldTuning,
  eps = 0.02,
) => {
  const fx1 = fissureMask01(nx + eps, ny, seed, tuning);
  const fx0 = fissureMask01(nx - eps, ny, seed, tuning);
  const fy1 = fissureMask01(nx, ny + eps, seed, tuning);
  const fy0 = fissureMask01(nx, ny - eps, seed, tuning);

  const dx = fx1 - fx0;
  const dy = fy1 - fy0;

  return { dx, dy };
};